import axios from "@/axiosInstance";
import ArchivedDevices from "@/components/ArchivedDevices.vue";
import Dashboard from "@/components/Dashboard.vue";
import ResetPasswordPage from "@/components/ResetPasswordPage.vue";
import { readCookie } from "@/utils/readCookie";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { useToast } from "vue-toast-notification";
import Account from "../components/Account.vue";
import Data from "../components/Data.vue";
import DevicesList from "../components/DevicesList.vue";
import DevicesPage from "../components/DevicesPage.vue";
import Editor from "../components/Editor.vue";
import LoginPage from "../components/LoginPage.vue";
import Records from "../components/MultiChartsPage.vue";
import OAuthSuccess from "../components/OauthSuccess.vue";
import LiveMaps from "../components/LiveMaps.vue";
import AlertsPage from "../components/AlertsPage.vue";
import ReportsPage from "@/components/ReportsPage.vue";
import ExternalServices from "@/views/ExternalServices.vue";
import ReturnTo from "@/components/ReturnTo.vue";

const toast = useToast();

// ---------------------------------------------------------------------------
// Mappa rotta → passi del tour
// ---------------------------------------------------------------------------
export const tourMap: Record<string, any[]> = {
  Dashboard: [
    {
      target: "#step-header",
      content: "Questa è la tua Dashboard principale.",
    },
    {
      target: "#step-map",
      content:
        "Qui vedi la mappa con la posizione dei dispositivi in tempo reale.",
    },
    {
      target: "#step-table",
      content: "E qui la tabella dei tuoi dispositivi registrati.",
    },
  ],
  Devices: [
    {
      target: "#devices-list",
      content:
        "Questa la pagina generale con la lista dei tuoi dispositivi registrati.",
    },
    {
      target: "#btn-new-device",
      content: "Cliccando qui puoi aggiungere un nuovo dispositivo.",
    },
  ],
  Editor: [
    {
      target: "#editor-form",
      content:
        "Questa la pagina di editor da dove puoi creare o modificare un dispositivo.",
    },
    {
      target: "#add-modules-anchor-pin",
      content:
        "Clicca con il tasto destro sullo sfondo per inserire un nuovo modulo; per cancellarlo, clicca sempre con il tasto destro sul modulo esistente",
      params: {
        placement: "bottom", // oppure 'bottom', 'top'
        offset: [0, 12],
        highlight: false,
      },
    },
    {
      target: "#toggle-button",
      content:
        "Usa questo bottone per passare dalla pagina di editing alla pagina di visualizzazione dati in tempo reale",
    },
    {
      target: "#exit-to-devices",
      content:
        "Clicca sul bottone 'Esci' per tornare alla lista dei dispositivi registrati",
    },
  ],
  Data: [
    {
      target: "#data-chart",
      content:
        "Questa la pagina relativa ai dati del dispositivo in tempo reale. Clicca sul singolo modulo per visualizzarli",
    },
    {
      target: "#toggle-button",
      content:
        "Usa questo bottone per passare dalla pagina di visualizzazione dati alla pagina di editing del dispositivo",
    },
    {
      target: "#exit-to-devices",
      content:
        "Clicca sul bottone 'Esci' per tornare alla lista dei dispositivi registrati",
    },
  ],
};

const routes: RouteRecordRaw[] = [
  // Redirect root path to authentication
  { path: "/", redirect: "/auth" },
  {
    path: "/return-to",
    name: "ReturnTo",
    component: ReturnTo,
    meta: { requiresAuth: true, hideSidebar: true, hideAiPrompt: true },
  },
  {
    path: "/auth",
    name: "Auth",
    component: LoginPage,
    meta: { requiresGuest: true, hideSidebar: true, hideAiPrompt: true },
    // meta: { requiresGuest: true },
  },
  {
    path: "/oauth-success",
    name: "OAuthSuccess",
    component: OAuthSuccess,
    meta: { requiresGuest: true, hideSidebar: true, hideAiPrompt: true },
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: ResetPasswordPage,
    meta: { requiresAuth: false, hideSidebar: true, hideAiPrompt: true },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
    meta: { requiresAuth: true, sidebarInitiallyOpen: true },
  },
  {
    path: "/live-maps",
    name: "LiveMaps",
    component: LiveMaps,
    meta: { requiresAuth: true },
  },
  {
    path: "/devices",
    component: DevicesPage,
    meta: { requiresAuth: true, sidebarInitiallyOpen: true },
    children: [
      { path: "", name: "Devices", component: DevicesList }, // /devices
      {
        path: "archived",
        name: "ArchivedDevices",
        component: ArchivedDevices,
        meta: { requiresAuth: true, sidebarInitiallyOpen: true },
      },
      // { path: "editor", name: "EditorNew", component: Editor, props: { isNew: true }, meta: { sidebarInitiallyOpen: false } }, // /devices/editor (senza id) per i new devices
      {
        path: "editor/:deviceId?",
        name: "Editor",
        component: Editor,
        props: true,
        alias: ["/editor/:deviceId?"],
        meta: { sidebarInitiallyOpen: false },
      }, // /devices/editor con id opzionale
      {
        path: "data/:deviceId?",
        name: "Data",
        component: Data,
        props: true,
        alias: ["/data/:deviceId?"],
        meta: { sidebarInitiallyOpen: false },
      }, // /devices/data con id opzionale

      {
        path: "records/:deviceId?",
        name: "Records",
        component: Records,
        props: true,
        alias: ["/records/:deviceId?"],
        meta: { requiresAuth: true, sidebarInitiallyOpen: false },
      },
      {
        path: "track/:deviceId?",
        name: "Track",
        redirect: (to) => {
          return { name: "Data", params: to.params };
        },
        alias: ["/track/:deviceId?"],
        meta: { requiresAuth: true, sidebarInitiallyOpen: false },
      },
      {
        path: "reports/:deviceId?",
        name: "Reports",
        component: ReportsPage,
        props: true,
        alias: ["/reports/:deviceId?"],
        meta: { requiresAuth: true, sidebarInitiallyOpen: false },
      },
    ],
  },
  {
    path: "/account",
    name: "Account",
    component: Account,
    meta: { requiresAuth: true, sidebarInitiallyOpen: false },
  },
  {
    path: "/alerts",
    name: "Alerts",
    component: AlertsPage,
    meta: { requiresAuth: true, sidebarInitiallyOpen: true },
  },
  {
    path: "/external-services",
    name: "ExternalServices",
    component: ExternalServices,
    meta: { requiresAuth: true, sidebarInitiallyOpen: true },
  },

  /*  redirect dai vecchi URL (opzionale)  */
  { path: "/editor", redirect: "/devices/editor" },
  { path: "/data", redirect: "/devices/data" },

  // qualunque altra rotta → /auth
  { path: "/:pathMatch(.*)*", redirect: "/auth" },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

/* ------------------------------------------------------------------ */
/*  Navigation-guard globale                                          */
/* ------------------------------------------------------------------ */

// ENDPOINT che il BE usa per dirci se il cookie JWT è valido
// Deve restituire 200 con info utente -- altrimenti 401/403.
const WHO_AM_I = "/auth/me";

let cachedAuth = false; // micro-cache in memoria
// let cachedAuth = !!localStorage.getItem("currentUserId");
// window.addEventListener("auth-ready", () => {
// 	cachedAuth = !!localStorage.getItem("currentUserId");
// });
let fetchedOnce = false; // evitiamo richieste duplicate

export const setAuth = (val: boolean) => {
  cachedAuth = val;
};
export const authIsCached = () => cachedAuth;

router.beforeEach(async (to) => {
  const needsGuest = to.meta.requiresGuest;
  const needsAuth = to.meta.requiresAuth;

  // Bypass per /oauth-success con ?token
  // if (to.name === "OAuthSuccess" && to.query.token) return true;
  if (to.name === "OAuthSuccess") {
    setAuth(true); // fallback di sicurezza (opzionale)
    return true;
  }

  // rotta “pubblica” → avanti
  if (!needsGuest && !needsAuth) return true;

  /* se non abbiamo ancora verificato il cookie, chiamiamo il BE una sola volta */
  if (!fetchedOnce) {
    try {
      await axios.get(WHO_AM_I, { withCredentials: true }); // il cookie viaggia in automatico
      setAuth(true);
    } catch (err) {
      console.error(err);
      setAuth(false);
    } finally {
      fetchedOnce = true;
    }
  }

  // --- logica redirect ---
  if (needsAuth && !authIsCached()) return { name: "Auth" }; // deve loggarsi
  if (needsGuest && authIsCached()) {
    // se c'è un returnTo, prendi il ticket e vai all'editor
    const returnTo = to.query.returnTo as string | undefined;
    if (returnTo) {
      // Invece di fare tutto qui, redirecta alla rotta dedicata
      return {
        name: "ReturnTo",
        query: { returnTo }
      };
    }
    return { name: "Dashboard" }; // è già loggato
  }
  return true; // ok, continua
});

/** provider → etichetta leggibile  */
const OAUTH_LABEL: Record<string, string> = {
  google: "Google",
  linkedin: "LinkedIn",
  github: "GitHub",
};

router.afterEach(async (to) => {
  // ——— Trigger tour (PRIMA di eventuali early‑return) ———
  // console.log("[router] afterEach →", to.name);
  window.dispatchEvent(
    new CustomEvent("route-changed", { detail: to.name as string })
  );

  /* ---------- ① NUOVA REGISTRAZIONE via provider ---------- */
  const justRegistered = readCookie("justRegistered");
  if (justRegistered) {
    /* 1. cancella il cookie (one-shot) */
    document.cookie = "justRegistered=; Max-Age=0; path=/";

    /* 2. provider leggibile dal query-param ?auth=… (se c’è) */
    const provider = to.query.auth as string | undefined;
    const label =
      provider && provider in OAUTH_LABEL
        ? ` con ${OAUTH_LABEL[provider]}`
        : "";

    /* 3. prendi username/email appena salvati */
    let who = "";
    try {
      const { data } = await axios.get(WHO_AM_I, { withCredentials: true });
      who = `'${data.username || data.email}'! `;
    } catch {
      who = "";
    }

    /* 4. toast personalizzato */
    toast.success(
      `Benvenuto ${who}Registrazione completata${label}. ` +
      `Controlla la tua e-mail: ti abbiamo inviato la password iniziale.`,
      { duration: 5000, position: "top-right" }
    );

    /* 5. pulizia URL */
    const { auth, ...rest } = to.query as Record<string, any>;
    if (auth) router.replace({ path: to.path, query: rest });

    return; // stop: evita il toast “Bentornato”
  }

  /* ---------- login annullato ---------- */
  const cancel = to.query.cancel as string | undefined;
  if (cancel && cancel in OAUTH_LABEL) {
    toast.info(
      `Autenticazione con ${OAUTH_LABEL[cancel]} annullata. Nessun account è stato collegato.`,
      { duration: 4000, position: "top-right" }
    );
    /* pulizia URL */
    const { cancel: _c, ...rest } = to.query as Record<string, any>;
    router.replace({ path: to.path, query: rest });
    return;
  }

  /* ---------- login riuscito ---------- */
  if (to.name === "OAuthSuccess") return; // sarà gestito dal componente

  const provider = to.query.auth as string | undefined;
  if (!provider || !(provider in OAUTH_LABEL)) return;

  try {
    const { data } = await axios.get(WHO_AM_I, { withCredentials: true });
    setAuth(true);
    toast.success(
      `Bentornato ${data.username || data.email}! Accesso con ${OAUTH_LABEL[provider]
      } riuscito.`,
      { duration: 4000, position: "top-right" }
    );
  } catch {
    toast.success(`Accesso con ${OAUTH_LABEL[provider]} riuscito!`, {
      duration: 4000,
      position: "top-right",
    });
  }

  /* pulizia URL */
  const { auth, ...rest } = to.query;
  router.replace({ path: to.path, query: rest });
});

export default router;
